import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from '@/App';
import AppErrorBoundary from '@/components/AppErrorBoundary';
import '@/index.css';

// ─── Phase 43 — Uncaught-JS error auto-reporter ───────────────────────
// Any error that escapes React (event handlers, async promises, third
// party libs) gets POSTed to /api/errors/client so admin can spot it in
// the System Errors feed instead of the user silently seeing a blank
// screen. Uses `fetch` directly (not axios) so it works even if the
// axios interceptor itself was the crash source.
// ──────────────────────────────────────────────────────────────────────
try {
    const _reported = new Set();
    const post = (msg, stack, kind, path) => {
        const key = `${kind}|${(msg || '').slice(0, 100)}`;
        if (_reported.has(key)) return;
        _reported.add(key);
        setTimeout(() => _reported.delete(key), 30000);
        try {
            const backend = process.env.REACT_APP_BACKEND_URL || '';
            fetch(`${backend}/api/errors/client`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    ...(localStorage.getItem('surya_token')
                        ? { Authorization: `Bearer ${localStorage.getItem('surya_token')}` }
                        : {}),
                },
                body: JSON.stringify({
                    message: (msg || 'Unknown error').slice(0, 800),
                    kind:    kind || 'UncaughtError',
                    path:    path || window.location.pathname,
                    stack:   (stack || '').slice(0, 5000),
                    extra:   { ua: navigator.userAgent.slice(0, 200) },
                }),
                keepalive: true,
            }).catch(() => {});
        } catch { /* never crash the reporter itself */ }
    };
    window.addEventListener('error', (e) => {
        post(e?.message, e?.error?.stack, e?.error?.name || 'WindowError', window.location.pathname);
    });
    window.addEventListener('unhandledrejection', (e) => {
        const r = e?.reason;
        const msg   = r?.message || (typeof r === 'string' ? r : 'Unhandled promise rejection');
        const stack = r?.stack || '';
        post(msg, stack, r?.name || 'UnhandledRejection', window.location.pathname);
    });
} catch { /* ignore — very old browsers */ }

// Phase 12i: Flag the running platform. When packaged inside Capacitor the
// `native-app` class unlocks the SolarSquare/Terra Rex inspired theme in
// index.css — regular browsers keep the original UPPOWER Solar website look.
try {
    const isNative = !!(window.Capacitor && (
        (typeof window.Capacitor.isNativePlatform === 'function' && window.Capacitor.isNativePlatform())
        || window.Capacitor.getPlatform && window.Capacitor.getPlatform() !== 'web'
    ));
    if (isNative) {
        document.documentElement.classList.add('native-app');
    }
} catch { /* ignore — desktop browsers won't have window.Capacitor */ }

createRoot(document.getElementById('root')).render(
    <StrictMode>
        <AppErrorBoundary>
            <App />
        </AppErrorBoundary>
    </StrictMode>
);

// Register the PWA service worker (production only, and never inside Capacitor native shell)
if ('serviceWorker' in navigator && window.location.protocol === 'https:' && !window.Capacitor) {
    window.addEventListener('load', () => {
        navigator.serviceWorker
            .register('/service-worker.js')
            .catch((err) => console.warn('SW registration failed:', err));
    });
}
